// Есть ли связь с сервером — и что показывать человеку, пока её нет.
//
// Источники: сетевые ошибки запросов (ответа нет вовсе или вышел таймаут —
// services/api.js) и события online/offline браузера. Любой ответ сервера,
// даже 500, значит «связь есть».
//
// До 14.10.2026 здесь был один флаг offline, а у запросов не было таймаута:
// телефон терял сеть незаметно (iPhone после сна, смена Wi-Fi на сотовую),
// запрос висел, ничего не падало — и человек видел вечное «Сохраняю…» без
// единой подсказки. Теперь: таймауты в api.js превращают зависание в
// понятный отказ; шторка (OfflineBanner.vue) отличает «нет интернета на
// телефоне» от «сервер не отвечает»; медленный запрос подсвечивается в
// кнопке; неотправленное сохранение уходит само при возврате связи
// (registerPendingSave — повтор безопасен, у записей есть client_id).
//
// Без pinia намеренно: этот модуль импортирует api.js, а api.js импортируют
// все стора — pinia здесь дала бы цикл.
import { ref } from 'vue'

const hasWindow = typeof window !== 'undefined'
const deviceOffline = () => typeof navigator !== 'undefined' && navigator.onLine === false

// Связи нет (по отказу запроса или по слову браузера)
export const offline = ref(deviceOffline())
// Почему: 'device' — интернета нет на телефоне, 'server' — интернет есть, а
// сервер не отвечает. null, пока связь есть.
export const reason = ref(offline.value ? 'device' : null)
// Связь только что вернулась — короткое «Связь восстановлена»
export const restored = ref(false)
// Неотправленное сохранение уходит прямо сейчас / только что ушло
export const syncing = ref(false)
export const synced = ref(false)
// Есть запрос, который идёт дольше SLOW_MS — «связь медленная»
export const slow = ref(false)

const timings = { slowMs: 5000, restoredMs: 2500, syncedMs: 2500 }

const reconnectHandlers = new Set()
const pendingSaves = new Map()   // key → async () => void

let restoredTimer = null
let syncedTimer = null

export function markOffline(why) {
  reason.value = why === 'device' || why === 'server' ? why : (deviceOffline() ? 'device' : 'server')
  if (offline.value) return
  offline.value = true
  restored.value = false
  if (restoredTimer) { clearTimeout(restoredTimer); restoredTimer = null }
}

export async function markOnline() {
  if (!offline.value) return
  offline.value = false
  reason.value = null
  restored.value = true
  if (restoredTimer) clearTimeout(restoredTimer)
  restoredTimer = setTimeout(() => { restored.value = false; restoredTimer = null }, timings.restoredMs)
  for (const h of reconnectHandlers) {
    try { h() } catch {}
  }
  await runPendingSaves()
}

// Подписка на «связь вернулась» — дневник по ней перечитывает день.
export function onReconnect(handler) {
  reconnectHandlers.add(handler)
  return () => reconnectHandlers.delete(handler)
}

// Сохранение, которое не ушло из-за обрыва: окно остаётся открытым и
// регистрирует повтор. Ключ — само окно (объект или Symbol), чтобы повторное
// нажатие в том же окне заменяло запись, а не копило их. Снимается при
// успехе, закрытии окна и размонтировании.
export function registerPendingSave(key, fn) {
  pendingSaves.set(key, fn)
}

export function unregisterPendingSave(key) {
  pendingSaves.delete(key)
}

export function hasPendingSaves() {
  return pendingSaves.size > 0
}

async function runPendingSaves() {
  if (pendingSaves.size === 0) return
  const jobs = [...pendingSaves.entries()]
  syncing.value = true
  let ok = 0
  for (const [key, fn] of jobs) {
    // Повтор снимает регистрацию сам при успехе; при новом обрыве окно
    // зарегистрирует её заново, и она дождётся следующего возврата связи.
    pendingSaves.delete(key)
    try { await fn(); ok++ } catch {}
  }
  syncing.value = false
  if (ok > 0) {
    synced.value = true
    if (syncedTimer) clearTimeout(syncedTimer)
    syncedTimer = setTimeout(() => { synced.value = false; syncedTimer = null }, timings.syncedMs)
  }
}

// Учёт долгих запросов: api.js вызывает в начале запроса и получает
// функцию завершения. Пока хоть один запрос идёт дольше slowMs — slow.
let slowCount = 0
export function beginRequest() {
  let counted = false
  let timer = setTimeout(() => {
    counted = true
    slowCount++
    slow.value = true
  }, timings.slowMs)
  return () => {
    if (timer) { clearTimeout(timer); timer = null }
    if (counted) {
      counted = false
      slowCount = Math.max(0, slowCount - 1)
      if (slowCount === 0) slow.value = false
    }
  }
}

// Для тестов: ускорить таймеры и сбросить состояние
export function _setTimings(t) { Object.assign(timings, t) }
export function _reset() {
  offline.value = deviceOffline()
  reason.value = offline.value ? 'device' : null
  restored.value = false
  syncing.value = false
  synced.value = false
  slow.value = false
  slowCount = 0
  pendingSaves.clear()
  reconnectHandlers.clear()
}

if (hasWindow) {
  window.addEventListener('offline', () => markOffline('device'))
  // 'online' — интерфейс поднялся, сервер ещё не отвечал. Снимаем шторку
  // оптимистично: первый же обрыв вернёт её.
  window.addEventListener('online', () => { markOnline() })
  // Возврат в приложение: iOS-PWA не всегда присылает 'offline' в фоне
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && deviceOffline()) markOffline('device')
    })
  }
}
